/* import __COLOCATED_TEMPLATE__ from './audience-selector.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import { helper } from '@ember/component/helper';
import { inject as service } from '@ember/service';

interface Signature {
  Args: {
    segments: AiContentSegment[];
    selectedSegmentIds: Set<string>;
    isLoading: boolean;
    isDisabled: boolean;
    onSelectSegment: (segment: AiContentSegment) => void;
    onClearSegments: () => void;
    onTransitionToAudienceSettings: () => void;
  };
}

export default class AudienceSelector extends Component<Signature> {
  @service declare intl: $TSFixMe;

  get normalizedSelectedSegmentIds(): Set<string> {
    return this.args.selectedSegmentIds instanceof Set
      ? this.args.selectedSegmentIds
      : new Set(this.args.selectedSegmentIds);
  }

  get dropdownLabel() {
    let selectedIds = Array.from(this.normalizedSelectedSegmentIds);
    if (selectedIds.length === 0) {
      return this.intl.t('knowledge-hub.content-editor.shared.audience-section.dropdown.everyone');
    }

    let selectedSegments = selectedIds
      .map((id) => this.args.segments.find((segment) => segment.id === id)?.nameWithFallback)
      .filter(Boolean);

    if (selectedSegments.length <= 2) {
      return selectedSegments.join(', ');
    }

    let firstTwo = selectedSegments.slice(0, 2);
    let remainingCount = selectedSegments.length - 2;
    return `${firstTwo.join(', ')} + ${remainingCount} more`;
  }

  segmentHasSegmentationRule = helper(function ([segment, selectedSegmentIds]: [
    AiContentSegment,
    Set<string>,
  ]) {
    return selectedSegmentIds.has(segment.id);
  });

  @action
  selectSegment(segment: AiContentSegment) {
    this.args.onSelectSegment(segment);
  }

  @action
  clearSegments() {
    this.args.onClearSegments();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::AudienceSelector': typeof AudienceSelector;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/audience-selector': typeof AudienceSelector;
  }
}
